import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { imagesApi } from '@/api/images.api';
import {
  institutionBrandingToPlayerCssVars,
  isInstPlayerThemeDark,
  mergeInstitutionThemeForHighContrast,
  mergeInstitutionThemeForUiMode,
  pickInstitutionLogoPath,
  type InstitutionPlayerBrandingPayload,
} from '@/lib/institution-player-branding';
import { useUiThemeDark } from '@/hooks/useUiThemeDark';
import {
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';
import { readA11ySettingsFromStorage } from '@/lib/lyrra-a11y-settings';
import A11yToolbar from '@/components/accessibility/A11yToolbar';
import '@/styles/institution-shell-dark.css';
import '@/styles/institution-shell-high-contrast.css';
import '@/styles/trainer-shell-light.css';
import '@/styles/lyrraone-trainer-theme.css';
import '@/styles/lyrraone-marketing-pages.css';
import { isLyrraOne } from '@/lib/platform';

type InstitutionAppThemeContextValue = {
  institutionLogoSrc: string | null;
  institutionLogoSmallSrc: string | null;
  institutionBranding: InstitutionPlayerBrandingPayload | null;
  uiThemeDark: boolean;
};

const InstitutionAppThemeContext = createContext<InstitutionAppThemeContextValue>({
  institutionLogoSrc: null,
  institutionLogoSmallSrc: null,
  institutionBranding: null,
  uiThemeDark: false,
});

export function useInstitutionAppTheme() {
  return useContext(InstitutionAppThemeContext);
}

/**
 * Applique la personnalisation institution (couleurs + logo) au shell concepteur / AppLayout.
 * Réutilise les mêmes variables CSS que le player et l’espace apprenant.
 */
export function InstitutionAppThemeLayout({
  children,
  includeA11yToolbar = false,
}: {
  children: React.ReactNode;
  /** true sur /institution (hors AppLayout) — évite un doublon avec la toolbar du formateur. */
  includeA11yToolbar?: boolean;
}) {
  const isDark = useUiThemeDark();
  const [highContrast, setHighContrast] = useState(
    () => readA11ySettingsFromStorage().highContrast,
  );

  useEffect(() => {
    const sync = () => setHighContrast(readA11ySettingsFromStorage().highContrast);
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const brandingStyle = useMemo(
    () => institutionBrandingToPlayerCssVars(null),
    [],
  );

  const themeStyle = useMemo(() => {
    if (highContrast) return mergeInstitutionThemeForHighContrast(brandingStyle);
    return mergeInstitutionThemeForUiMode(brandingStyle, isDark);
  }, [brandingStyle, isDark, highContrast]);

  const playerThemeDark = useMemo(() => {
    if (highContrast) return false;
    return isInstPlayerThemeDark(themeStyle as Record<string, string>) || isDark;
  }, [themeStyle, isDark, highContrast]);

  /** Portails (Modal, etc.) sont sous <body> : miroir des variables sur :root pour hériter du thème institution. */
  useEffect(() => {
    const el = document.documentElement;
    const entries = Object.entries(themeStyle as Record<string, unknown>).filter(
      (e): e is [string, string] => typeof e[1] === 'string' && e[0].startsWith('--'),
    );
    for (const [key, value] of entries) {
      el.style.setProperty(key, value);
    }
    return () => {
      for (const [key] of entries) {
        el.style.removeProperty(key);
      }
    };
  }, [themeStyle]);

  const logoUsesDarkVariant = isDark && !highContrast;

  const institutionLogoSrc = useMemo(() => {
    const raw = pickInstitutionLogoPath(null, { isDark: logoUsesDarkVariant, collapsed: false });
    return raw ? imagesApi.resolveUrl(raw) : null;
  }, [logoUsesDarkVariant]);

  const institutionLogoSmallSrc = useMemo(() => {
    const raw = pickInstitutionLogoPath(null, { isDark: logoUsesDarkVariant, collapsed: true });
    return raw ? imagesApi.resolveUrl(raw) : null;
  }, [logoUsesDarkVariant]);

  const ctx = useMemo(
    () => ({ institutionLogoSrc, institutionLogoSmallSrc, institutionBranding: null, uiThemeDark: isDark }),
    [institutionLogoSrc, institutionLogoSmallSrc, isDark],
  );

  return (
    <InstitutionAppThemeContext.Provider value={ctx}>
      <div
        data-lyrraone-trainer={isLyrraOne() ? 'true' : undefined}
        data-inst-player-dark={playerThemeDark ? 'true' : undefined}
        data-ui-theme={highContrast || !isDark ? 'light' : 'dark'}
        data-a11y-high-contrast={highContrast ? 'true' : undefined}
        className="player-institution-theme flex h-screen max-h-dvh min-h-0 w-full flex-row overflow-hidden bg-[color:var(--inst-player-bg,#f9fafb)] text-[color:var(--inst-player-text,#0C1C38)]"
        style={themeStyle}
      >
        {children}
        {includeA11yToolbar ? (
          <A11yToolbar fabPosition="left" panelPosition="left" />
        ) : null}
      </div>
    </InstitutionAppThemeContext.Provider>
  );
}
